import {DrawerProps} from '@mantine/core';

import {FormDrawer} from './FormDrawer';
import {useCreateZoneMutation} from '../hooks/useZoneMutations';
import {CreateDnsZoneInput} from '../types';

// FNP-1191 CRUD half — Variant A of the drawer pattern (a parent entity, no
// parent id from the route).
//
// ⚠️ THE CONNECTION IS NOT A PARENT ID FROM `useParams`, WHICH IS WHY THIS IS
// VARIANT A AND NOT VARIANT B.
//
// The doc's Variant B pulls a parent id off the URL because the child's routes
// are nested under the parent's. Zones are NOT nested under a connection: the
// index lists zones across EVERY connection a tenant has, so there is no
// connection in the route to inherit. The operator chooses one in the form, and
// the picker offers only connections that can actually create a zone — see
// hooks/useZoneCreateConnections.

export const AddFormDrawer = (props: DrawerProps) => {

    const createMutation = useCreateZoneMutation();

    // ⚠️ AWAITED, AND A FAILURE PROPAGATES. `mutate` is fire-and-forget, so
    // FormDrawer's `await handleSubmit(...)` would resolve immediately and the
    // drawer would close over a rejected create — taking the operator's typed
    // values with it and leaving only a toast to explain a form they can no
    // longer correct. `mutateAsync` rejects, FormDrawer never reaches its
    // `onClose()`, and the drawer stays open with the input intact.
    //
    // The rejection is NOT caught here. The mutation's own `onError` has
    // already classified and notified it; catching it again would either
    // swallow the signal FormDrawer needs to stay open, or re-report a failure
    // the user has already been told about.
    const handleSubmit = async (values: CreateDnsZoneInput) => {
        await createMutation.mutateAsync({
            connectionId: values.connectionId,
            name: values.name.trim(),
            // Omitted entirely when blank rather than sent as an empty string —
            // the field is optional on the input, and an empty comment is not
            // the same as no comment to a provider that stores one.
            ...(values.comment && values.comment.trim()
                ? {comment: values.comment.trim()}
                : {}),
        });
    };

    return (
        <FormDrawer
            record={{
                connectionId: '',
                name: '',
                comment: '',
            }}
            handleSubmit={handleSubmit}
            {...props}
        />
    );
};
